import {getRequest} from '@/Services/ApiService';
import {reportError} from '@/Services/Logger';
import APIURL from '@/api-url';
import styles from '@/styles/blogDetail.module.scss';
import {BlogLinkRedirect} from '@/util/Blog-link';
import {MUIBox, MUIContainer, MUIGrid, MUISpinner, MUITypography} from '@/components/MUI-Components';
import {NextPageContext} from 'next';
import dynamic from 'next/dynamic';
import PageHead from '@/components/PageHead';
import JsonLd from '@/components/Common/JsonLd';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import parse from 'html-react-parser';
// Declared after `import dynamic` on purpose: Next only adds its preload metadata to dynamic() calls
// that follow the import. Declared above it, CommentForm was not preloaded and the page failed hydration.
const CommentForm = dynamic(() => import('@/components/Ui-components/CommentForm'));
const ContactForm = dynamic(() => import('@/components/Ui-components/Form'));
import {fixSchemaDomain, cleanFaqNodes, buildBlogPostingSchema, buildBreadcrumbSchema} from '@/util/seo-utils';

import {useScrollHighlight, scrollToHeading} from '@/components/blog-shared/TableOfContents';
import RelatedPosts from '@/components/blog-shared/RelatedPosts';
import {
    splitHtmlByRelatedPostsShortcode,
    stripHtml,
    decodeHtmlEntities,
    addTargetBlank,
    unwrapCodeWrappedCtaBlocks,
    addFaqQuestionClass,
    faqQuestionToH3,
    addTableClasses,
    addImageClasses,
    addHeadingClasses
} from '@/util/html-parser-utils';
import {
    makeRelatedCardParseOptions,
    makeReadMoreParseOptions,
    makeCtaBlocksParseOptions,
    replaceContentImage
} from '@/components/blog-shared/html-parser';
import {InlineRelatedPost} from '@/components/blog-shared/RelatedPosts';
import {SITE_URL} from '@/constants/urls';
import {resolveReviewer} from '@/constants/reviewer';
import BlogBreadcrumb from '@/components/blog-shared/blog-detail/BlogBreadcrumb';
import BlogHero from '@/components/blog-shared/blog-detail/BlogHero';
import BlogAuthorBar from '@/components/blog-shared/blog-detail/BlogAuthorBar';
import BlogMediaBar from '@/components/blog-shared/blog-detail/BlogMediaBar';
import BlogTocSidebar from '@/components/blog-shared/blog-detail/BlogTocSidebar';
import BlogKeyTakeaways from '@/components/blog-shared/blog-detail/BlogKeyTakeaways';
import BlogAuthorCards from '@/components/blog-shared/blog-detail/BlogAuthorCards';
import AiToolsCard from '@/components/shared/cards/AiToolsCard';
import BlogReviewSlider from '@/components/blog-shared/blog-detail/BlogReviewSlider';
import BlogComments from '@/components/blog-shared/blog-detail/BlogComments';
import {blogExpertFormWrapperSx} from '@/styles/blogExpertFormWrapperSx';
import {commentFormWrapperSx} from '@/styles/commentFormWrapperSx';
import {setEdgeCache} from '@/util/edgeCache';

// ─── Module-level style constants ─────────────────────────────────────────────
// stretch lets the sidebar column fill the row height, giving the sticky
// form/slider group room to travel as the article scrolls.
const stickyGridSx = {alignSelf: 'stretch'} as const;
// ─────────────────────────────────────────────────────────────────────────────

const BlogDetailNew = (props: any) => {
    const {blogData} = props;

    const authorInfo = blogData?.author_info ?? {name: '', image: '', bio: '', linkedin: '', designation: ''};
    // WP-selected reviewer wins; fall back to the default (Harry) when none is set.
    const reviewerInfo = resolveReviewer(blogData?.reviewer_info);
    const heroImage = blogData?.featured_image_url || blogData?.blog_image || '';
    const categories = blogData?.unique_categories || blogData?.selected_categories || [];
    const readTime = blogData?.read_time ?? '';
    const viewCount = blogData?.view_count ?? null;
    const keyTakeaways: string[] = blogData?.key_takeaways ?? [];
    const tableOfContents: {level: number; text: string; slug: string}[] = blogData?.table_of_contents ?? [];

    // Structured CTA from backend. Prefer the newer `cta_blocks` array
    // ({badge, title, description, button_text, button_url}), fall back to the
    // legacy `cta` ({tag, title, description, btn_label, btn_href}).
    // The card only renders when the backend supplies a title + button (`hasCta`).
    // Backend sends `cta_blocks`: an ordered array — one entry per [cta_block_N]
    // shortcode in the content ([cta_block_1] → cta_blocks[0], etc.). Fall back to
    // the legacy single `cta` object ({tag, title, description, btn_label, btn_href}).
    // Derived purely from the (stable) server payload, so memoize on `blogData` to
    // keep a stable array reference for the content-render memo below.
    const ctaBlocks = useMemo(() => {
        const rawCtaBlocks: any[] = Array.isArray(blogData?.cta_blocks) ? blogData.cta_blocks : [];
        const ctaData = (typeof blogData?.cta === 'object' && blogData?.cta) || {};
        return rawCtaBlocks.length > 0
            ? rawCtaBlocks.map((b: any) => ({
                  tag: b?.badge || '',
                  title: b?.title || '',
                  description: b?.description || '',
                  btnLabel: b?.button_text || '',
                  btnHref: b?.button_url || ''
              }))
            : [
                  {
                      tag: ctaData.tag || '',
                      title: ctaData.title || '',
                      description: ctaData.description || '',
                      btnLabel: ctaData.btn_label || '',
                      btnHref: ctaData.btn_href || ''
                  }
              ];
    }, [blogData]);

    const blogCategoryName = categories.filter((item: any) => item !== 'All Topics')?.[0] ?? '';
    const blogCategoryLink = blogCategoryName.replace(/[\s/]+/g, '-').toLowerCase();

    const title = typeof blogData?.title === 'string' ? blogData.title : blogData?.title?.rendered ?? '';
    const excerpt = decodeHtmlEntities(
        blogData?.yoast_head_json?.description ||
            (blogData?.excerpt?.rendered
                ? stripHtml(blogData.excerpt.rendered)
                      .replace(/\[…\]|\[\.\.\.\]/g, '')
                      .trim()
                : '')
    );

    // ── Structured data schemas (pure builders in seo-utils, reusable + testable) ─
    const blogPostingSchema = buildBlogPostingSchema(blogData, {title, heroImage, authorInfo});
    const breadcrumbSchema = buildBreadcrumbSchema(blogData, {title, blogCategoryName, blogCategoryLink});

    // Yoast schema graph, serialized for PageHead's `schema` prop.
    const yoastSchema = blogData?.yoast_head_json?.schema
        ? JSON.stringify(
              fixSchemaDomain({
                  ...blogData.yoast_head_json.schema,
                  '@graph': cleanFaqNodes(blogData.yoast_head_json.schema['@graph'])
              })
          )
        : undefined;
    // ─────────────────────────────────────────────────────────────────────────

    const currentBlogId = blogData?.id;
    const {categoryResponse, latestInsights, inlineRelatedPosts = [], readLinkPosts = []} = props;
    const rawContent = blogData?.content?.rendered ?? '';
    const hasDebutCard = rawContent.includes('debut-related-card');
    const hasRawShortcode = /\[related_post(?:_\d+)?\]/.test(rawContent);
    const hasInlineCtaBlock = /\[cta_block(?:_\d+)?\]/.test(rawContent) || rawContent.includes('debut-cta-block');
    const [commentData, setCommentData] = useState<any>();
    const [tocItems, setTocItems] = useState<string[]>([]);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const tocListRef = useRef<HTMLElement | null>(null);
    const [loading, setLoading] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [instagramMsg, setInstagramMsg] = useState('');

    const setActiveIndexStable = useCallback((i: number) => setActiveIndex(i), []);
    useScrollHighlight(setActiveIndexStable);

    useEffect(() => {
        if (activeIndex === null || !tocListRef.current) return;
        const activeItem = tocListRef.current.children[activeIndex] as HTMLElement;
        if (activeItem) {
            activeItem.scrollIntoView({block: 'nearest', behavior: 'smooth'});
        }
    }, [activeIndex]);

    useEffect(() => {
        if (!blogData) return;
        setLoading(true);

        // Decodes HTML entities (e.g. backend sends "Introduction&nbsp;") and trims,
        // so the TOC never shows raw entity text.
        const decodeEntities = (s: string) => {
            const el = document.createElement('textarea');
            el.innerHTML = s;
            return (el.value || '').replace(/ /g, ' ').trim();
        };

        const toc: {level: number; text: string; slug: string}[] = blogData.table_of_contents ?? [];
        if (toc.length > 0) {
            setTocItems(toc.filter((i) => i.level === 2).map((i) => decodeEntities(i.text)));
        } else {
            const container = document.createElement('div');
            container.innerHTML = blogData?.content?.rendered ?? '';
            const extracted = Array.from(container.getElementsByTagName('h2')).map((el) => el.textContent ?? '');
            setTocItems(extracted);
        }

        if (!blogData.id) {
            setCommentData([]);
            setLoading(false);
            return;
        }

        getRequest(`${APIURL.COMMENT_LISTING}?post=${blogData.id}`)
            .then((res: any) => setCommentData(res))
            .catch(() => setCommentData([]))
            .finally(() => setLoading(false));
    }, [blogData]);

    // Increment view count once per page visit (fire-and-forget)
    useEffect(() => {
        if (!blogData?.id) return;
        getRequest(`${APIURL.BLOG_VIEW}/${blogData.id}/view`).catch((_err) => undefined);
    }, [blogData?.id]);

    // Desktop only: tie the sticky sidebar's internal scroll to the article's
    // scroll progress. The sidebar (AI card + form + review slider) is taller than
    // the viewport, so as the user reads the article it auto-scrolls in sync —
    // revealing the form/slider by the end instead of getting cut off.
    useEffect(() => {
        const sync = () => {
            if (globalThis.innerWidth < 1200) return;
            const sidebar = document.querySelector('.bdn-content-sidebar') as HTMLElement | null;
            const section = document.getElementById('bdn-description-section');
            if (!sidebar || !section) return;
            const extra = sidebar.scrollHeight - sidebar.clientHeight;
            if (extra <= 0) return;
            const range = section.offsetHeight - globalThis.innerHeight;
            const frac = range > 0 ? (globalThis.scrollY - section.offsetTop) / range : 0;
            sidebar.scrollTop = Math.min(extra, Math.max(0, frac * extra));
        };
        globalThis.addEventListener('scroll', sync, {passive: true});
        globalThis.addEventListener('resize', sync);
        sync();
        return () => {
            globalThis.removeEventListener('scroll', sync);
            globalThis.removeEventListener('resize', sync);
        };
    }, [blogData]);

    useEffect(() => {
        if (!blogData?.id) return;
        const saved = JSON.parse(localStorage.getItem('bdn-bookmarks') || '[]');
        setIsBookmarked(saved.includes(blogData.id));
    }, [blogData?.id]);

    const tocSlugs = tableOfContents.filter((i) => i.level === 2).map((i) => i.slug);

    const handleTocClick = useCallback(
        (index: number) => {
            setActiveIndex(index);
            const slug = tocSlugs[index];
            if (slug) {
                const el = document.getElementById(slug);
                if (el) {
                    el.scrollIntoView({behavior: 'smooth', block: 'start'});
                    return;
                }
            }
            scrollToHeading('bdn-description-section', index);
        },
        [tocSlugs]
    );

    const shareUrl = `${blogData?.link}`;

    const handleCopyLink = useCallback(() => {
        if (typeof navigator === 'undefined') return;
        navigator.clipboard.writeText(shareUrl).then(() => {
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        });
    }, [shareUrl]);

    const handleBookmark = useCallback(() => {
        if (!blogData?.id) return;
        const saved: number[] = JSON.parse(localStorage.getItem('bdn-bookmarks') || '[]');
        const updated = isBookmarked ? saved.filter((id) => id !== blogData.id) : [...saved, blogData.id];
        localStorage.setItem('bdn-bookmarks', JSON.stringify(updated));
        setIsBookmarked((prev) => !prev);
    }, [blogData?.id, isBookmarked]);

    const handleInstagramShare = useCallback(() => {
        if (typeof navigator === 'undefined') return;
        if (navigator.share) {
            navigator.share({title, text: excerpt, url: shareUrl}).catch((err) => reportError(err, {context: 'instagram-share'}));
        } else {
            navigator.clipboard.writeText(shareUrl).then(() => {
                setInstagramMsg('Link copied! Open Instagram and paste it in your story or bio.');
                setTimeout(() => setInstagramMsg(''), 3500);
                globalThis.open('https://www.instagram.com/', '_blank', 'noopener noreferrer');
            });
        }
    }, [shareUrl, title, excerpt]);

    // The transform chain + parse() is expensive over a full article. Its inputs all
    // derive from the (stable) server payload, so memoize it — otherwise it re-runs
    // on every scroll/slider/bookmark state change.
    const renderedContent = useMemo(() => {
        if (!rawContent) return null;
        // NOTE: no global stripAllStrong here — that would delete the bold WordPress
        // editors apply to keywords in body content (<p>, <li>, tables, etc.). Bold is
        // only stripped inside headings (addHeadingClasses); it's allowed everywhere else.
        const processedHtml = addImageClasses(
            addTableClasses(addHeadingClasses(addFaqQuestionClass(faqQuestionToH3(addTargetBlank(unwrapCodeWrappedCtaBlocks(rawContent))))))
        );

        // Single CTA replacer instance — its internal counter maps the Nth
        // <div class="debut-cta-block"> to ctaBlocks[N]. Reused across every parse()
        // pass below so numbering stays correct across chunks.
        const ctaOpts = makeCtaBlocksParseOptions(ctaBlocks);
        // "Read more – <a>..." paragraphs → bdn-related-section card design.
        const allPosts = [...(categoryResponse ?? []), ...(latestInsights ?? []), ...(readLinkPosts ?? [])];
        const readMoreOpts = makeReadMoreParseOptions(blogCategoryName, allPosts);

        // Merged replacer: runs readMore → cta in order. Used in all parse passes.
        const mergedReplace = (node: any, index: any) => {
            // Upgrade content <img> → next/image (responsive srcset) so body images stay sharp.
            const img = replaceContentImage(node);
            if (img !== undefined) return img;
            const rm = readMoreOpts.replace?.(node, index);
            if (rm !== undefined && rm !== false) return rm;
            return ctaOpts.replace?.(node, index);
        };

        // Case 1: WordPress processed the shortcode to debut-related-card HTML.
        // Merge in the cta-block replacer so both are handled in one parse pass.
        if (hasDebutCard) {
            const relatedOpts = makeRelatedCardParseOptions(blogCategoryName);
            return parse(processedHtml, {
                replace: (node, index) => {
                    const r = relatedOpts.replace?.(node, index);
                    if (r !== undefined && r !== false) return r;
                    return mergedReplace(node, index);
                }
            });
        }
        // Case 2: WordPress rendered [cta_block_N] as <div class="debut-cta-block">
        if (hasInlineCtaBlock) {
            return parse(processedHtml, {replace: mergedReplace});
        }
        // Case 3: Raw [related_post]/[related_post_N] shortcode text — split and inject
        // Only renders a card at the shortcode position when the WordPress team has
        // explicitly assigned a related post. No category fallback — empty shortcode = nothing.
        if (hasRawShortcode) {
            const chunks = splitHtmlByRelatedPostsShortcode(processedHtml);
            const explicitPosts = inlineRelatedPosts.filter((p: any) => p.id !== currentBlogId);
            if (chunks.length === 1) return parse(chunks[0], {replace: mergedReplace});
            return chunks.map((chunk, i) => (
                <React.Fragment key={i}>
                    {parse(chunk, {replace: mergedReplace})}
                    {i < chunks.length - 1 && explicitPosts[i] && (
                        <InlineRelatedPost item={explicitPosts[i]} basePath="/blog" categoryLabel={blogCategoryName} />
                    )}
                </React.Fragment>
            ));
        }
        // Case 4: No related shortcode — still replace any CTA blocks + read-more links
        return parse(processedHtml, {replace: mergedReplace});
    }, [
        rawContent,
        ctaBlocks,
        hasDebutCard,
        hasInlineCtaBlock,
        hasRawShortcode,
        blogCategoryName,
        categoryResponse,
        currentBlogId,
        inlineRelatedPosts,
        latestInsights,
        readLinkPosts
    ]);

    return (
        <>
            <PageHead
                title={blogData?.yoast_head_json?.title}
                description={blogData?.yoast_head_json?.description}
                image={heroImage}
                canonical={blogData?.yoast_head_json?.og_url ?? `${SITE_URL}/blog/${blogData?.slug}`}
                ogTitle={title}
                ogDescription={blogData?.meta?.description}
                ogLocale="en_US"
                schema={yoastSchema}
            />
            {blogPostingSchema && <JsonLd data={blogPostingSchema} />}
            {breadcrumbSchema && <JsonLd data={breadcrumbSchema} />}

            <MUIBox className={`${styles.bdnPage} bdn-page`}>
                {blogData?.title && (
                    <MUIContainer>
                        {/* ── Breadcrumb ── */}
                        <BlogBreadcrumb blogCategoryName={blogCategoryName} />

                        {/* ── Hero: title left, image right ── */}
                        <BlogHero
                            blogCategoryName={blogCategoryName}
                            title={title}
                            excerpt={excerpt}
                            blogData={blogData}
                            readTime={readTime}
                            heroImage={heroImage}
                        />

                        {/* ── Author bar ── */}
                        <BlogAuthorBar authorInfo={authorInfo} reviewerInfo={reviewerInfo} />

                        {/* ── Audio + Share bar ── */}
                        <BlogMediaBar
                            title={title}
                            html={blogData?.content?.rendered ?? ''}
                            viewCount={viewCount}
                            shareUrl={shareUrl}
                            instagramMsg={instagramMsg}
                            handleInstagramShare={handleInstagramShare}
                            isCopied={isCopied}
                            handleCopyLink={handleCopyLink}
                            isBookmarked={isBookmarked}
                            handleBookmark={handleBookmark}
                        />

                        {/* ── TOC + Content + Sidebar (reused from original) ── */}
                        <MUIBox className="tabel-blog-outer">
                            <MUIGrid container className="table-content-wrapper" spacing={{xs: 0, sm: 3}} gap={{xs: 2, sm: 0}}>
                                <MUIGrid item xs={12} sm={3} md={2.5} className="table-blog-content" sx={{alignSelf: 'flex-start'}}>
                                    <BlogTocSidebar
                                        tocItems={tocItems}
                                        activeIndex={activeIndex}
                                        handleTocClick={handleTocClick}
                                        tocListRef={tocListRef}
                                    />
                                </MUIGrid>
                                <MUIGrid item xs={12} sm={9} md={7} className="bdn-blog-content" id="bdn-description-section">
                                    {/* ── Key Takeaways card ── */}
                                    <BlogKeyTakeaways keyTakeaways={keyTakeaways} />
                                    {blogData?.content && renderedContent}

                                    {/* ── Author & Reviewer cards ── */}
                                    <BlogAuthorCards authorInfo={authorInfo} reviewerInfo={reviewerInfo} />
                                </MUIGrid>
                                <MUIGrid item xs={12} md={2.5} sx={stickyGridSx} className="bdn-content-sidebar" data-lenis-prevent>
                                    {/* ── AI-Powered Reading card ── */}
                                    <AiToolsCard title={title} url={shareUrl} />

                                    {/* ── Sticky group: form + sliders ── */}
                                    <MUIBox className="bdn-sticky-group" data-lenis-prevent>
                                        <MUIBox className="use-case-hupspot-form-wrapper" sx={blogExpertFormWrapperSx}>
                                            <ContactForm
                                                type="normal"
                                                message="Talk With Our Expert"
                                                submitBtn="Book a free call →"
                                                classNameForm="blog-side-form"
                                                submitBtnClass="button-bg-primary animated-button"
                                                smallForm={true}
                                            />
                                        </MUIBox>

                                        {/* ── Slider 1: Review platforms ── */}
                                        <BlogReviewSlider />
                                    </MUIBox>
                                </MUIGrid>
                            </MUIGrid>
                        </MUIBox>

                        {/* ── Related posts ── */}
                        <RelatedPosts
                            items={latestInsights ?? categoryResponse ?? []}
                            currentId={currentBlogId}
                            basePath="/blog"
                            heading="Our Latest Insights"
                        />

                        {/* ── Comments ── */}
                        <BlogComments commentData={commentData} />

                        {/* ── Comment form ── */}
                        <MUIBox className="margin" sx={commentFormWrapperSx}>
                            <MUITypography className="spectrum-heading color-black font-headers comment-form-heading">
                                Leave a Comment
                            </MUITypography>
                            <MUITypography className="comment-form-subheading">Your voice matters to us</MUITypography>
                            {blogData?.id && <CommentForm postId={blogData.id} />}
                        </MUIBox>
                    </MUIContainer>
                )}
            </MUIBox>

            {loading ? <MUISpinner /> : null}
        </>
    );
};

export {BlogDetailNew};
export default BlogDetailNew;
BlogDetailNew.Layout = 'Blog';

export const getServerSideProps: any = async (context: NextPageContext) => {
    const {query, res}: any = context;

    try {
        // ── Slug redirects (legacy URL cleanup) ───────────────────────────────
        let redirectionOccurred = false;
        BlogLinkRedirect.forEach((item) => {
            let queryParams = '';
            if (query.hasOwnProperty('page')) {
                queryParams = `${query?.blogsDetails.toLowerCase()}?page=${query?.page}`;
            } else if (query.hasOwnProperty('utm_source')) {
                queryParams = `${query?.blogsDetails.toLowerCase()}?utm_source=${query?.utm_source.toLowerCase()}`;
            } else {
                queryParams = query?.blogsDetails.toLowerCase();
            }
            if (queryParams === item?.externalLink.toLowerCase()) {
                res.writeHead(301, {Location: `${item.redirect}`});
                res.end();
                redirectionOccurred = true;
            }
        });
        if (redirectionOccurred) return {props: {}};

        // ── Fetch blog post ───────────────────────────────────────────────────
        // NOTE: no `_fields` filter here — WordPress strips custom REST fields
        // (author_info, reviewer_info, view_count, etc.) when `_fields` is used.
        // This is a single-post server-side fetch, so the full payload is fine.
        const response = await getRequest<any>(`${APIURL.BLOGS_DETAILS}?slug=${query?.blogsDetails}`);
        const blogData = response?.[0] || null;
        // Slug genuinely does not exist in the CMS → real HTTP 404 (not a 200 skeleton).
        // This is a `return`, not a `throw`, so it never falls through to the 500 handler below.
        if (!blogData) return {notFound: true};

        // ── News-category redirect ────────────────────────────────────────────
        if (blogData?.categories?.includes(115)) {
            return {redirect: {destination: `/news/${blogData.slug}`, permanent: true}};
        }

        // A real post with no category must still render — related posts are optional, never a 404/500 trigger.
        const id = blogData?.categories && blogData.categories.length > 0 ? blogData.categories.at(-1) : null;

        // ── Explicit related posts added by the WordPress team ────────────────
        // Passed as `inlineRelatedPosts` so the [related_post] shortcode positions
        // render ONLY when the team has set them. No category fallback here.
        const relatedPostObjects: any[] = Array.isArray(blogData?.related_posts) ? blogData.related_posts : [];
        const inlineRelatedPosts = relatedPostObjects.filter((p: any) => typeof p === 'object' && p?.id);

        // ── Category posts — for bottom "Our Latest Insights" grid only ────────
        // Optional supporting content: a failure degrades to null, never a 404/500.
        // Started now, awaited together with the "read more" lookup below (the two don't depend on each other).
        const categoryPostsRequest: Promise<any[] | null> = id
            ? (getRequest(
                  `${APIURL.BLOGS_LISTING}?categories=${id}&categories_exclude=115&per_page=6&_fields=id,title,slug,blog_image,featured_image_url,date,featured_media,unique_categories,custom_updated_date`
              ).catch(() => null) as Promise<any[] | null>)
            : Promise.resolve(null);

        // ── Thumbnails for inline "Read more / Read also" links ────────────────
        // These links often point to posts outside the current category (or to the
        // main-site domain), so they're not in categoryResponse. Pull the slugs out
        // of the content and fetch just those posts so the inline cards get a thumb.
        const rawContent: string = blogData?.content?.rendered ?? '';
        const readLinkSlugs: string[] = [];
        const pRegex = /<p\b[^>]*>([\s\S]*?)<\/p>/gi;
        let pMatch: RegExpExecArray | null;
        while ((pMatch = pRegex.exec(rawContent)) !== null) {
            const inner = pMatch[1];
            const lead = inner
                .replace(/<[^>]+>/g, '')
                .replace(/&#8211;|&nbsp;/g, ' ')
                .trim()
                .toLowerCase();
            if (!/^read (more|also)\b/.test(lead)) continue;
            const a = inner.match(/<a\b[^>]+href="([^"]+)"/i);
            if (!a) continue;
            let slug = '';
            try {
                slug = new URL(a[1]).pathname.replace(/\/$/, '').split('/').pop() ?? '';
            } catch {
                slug = a[1].replace(/\/$/, '').split('/').pop() ?? '';
            }
            if (slug) readLinkSlugs.push(slug);
        }

        const uniqueSlugs = [...new Set(readLinkSlugs)];
        const slugQuery = uniqueSlugs.map((s) => `slug[]=${encodeURIComponent(s)}`).join('&');
        const readLinkPostsRequest: Promise<any[]> = uniqueSlugs.length
            ? getRequest<any[]>(
                  `${APIURL.BLOGS_LISTING}?${slugQuery}&per_page=20&_fields=id,title,slug,blog_image,featured_image_url,unique_categories`
              ).then((posts) => posts || [])
            : Promise.resolve([]);

        const [categoryPosts, readLinkPosts] = await Promise.all([categoryPostsRequest, readLinkPostsRequest]);

        // categoryResponse: always category-based (used by bottom Related Posts section
        // and as image lookup pool for "Read more" inline links).
        const categoryResponse = categoryPosts;

        // latestInsights: bottom "Our Latest Insights" grid — exclude current post
        // and any explicitly-linked inline related posts to avoid duplication.
        let latestInsights: any[] | null = null;
        if (Array.isArray(categoryPosts)) {
            const relatedIds = new Set(inlineRelatedPosts.map((p: any) => p?.id));
            latestInsights = categoryPosts.filter((p: any) => p.id !== blogData.id && !relatedIds.has(p.id)).slice(0, 3);
        }

        setEdgeCache(res);
        return {
            props: {
                blogData,
                query,
                id,
                categoryResponse: categoryResponse || null,
                latestInsights: latestInsights || null,
                inlineRelatedPosts,
                readLinkPosts
            }
        };
    } catch (error) {
        // Reaching here means an infrastructure/transport failure (WordPress down, timeout, parse error) —
        // NOT "page missing". Returning notFound here would de-index live posts during an outage.
        // Emit HTTP 500 so Google retries instead of dropping the URL.
        reportError(error, {context: 'getServerSideProps:blogDetail'});
        if (res) res.statusCode = 500;
        return {
            props: {
                blogData: null,
                query,
                id: null,
                categoryResponse: null,
                latestInsights: null,
                inlineRelatedPosts: [],
                readLinkPosts: []
            }
        };
    }
};
